/**
 * JSON → TOML Tool
 *
 * Converts JSON into TOML.
 */

import { stringify as stringifyToml } from 'smol-toml';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';
import { parseJsonLenient } from '@/lib/jsonParser';

export const JsonToTomlTool: ITransformerTool = {
  id: 'json-to-toml',
  name: 'JSON → TOML',
  description: 'Convert JSON to TOML',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['json', 'toml', 'convert', 'transform', 'config'],

  seo: {
    slug: 'json-to-toml-converter',
    pageTitle: 'JSON to TOML Converter - Free Online Tool | Prism',
    metaDescription: 'Convert JSON to TOML configuration format instantly. Produces clean, readable TOML output. Fast, private, runs entirely in your browser.',
    keywords: ['json to toml', 'json converter', 'convert json to toml online', 'json toml tool'],
    relatedTools: ['toml-to-json', 'json-to-yaml', 'toml-validate'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.JSON],
  outputType: SEMANTIC_TYPES.TOML,

  attributes: [
    {
      name: 'sortKeys',
      type: 'boolean',
      label: 'Sort Keys',
      defaultValue: false,
      description: 'Sort object keys alphabetically',
    },
  ],

  supportsInput(input: string, detectedType?: string): boolean {
    if (detectedType !== 'JSON') return false;
    try {
      parseJsonLenient(input);
      return true;
    } catch {
      return false;
    }
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    let parsed: unknown;

    try {
      parsed = parseJsonLenient(input);
    } catch (e) {
      const error = e as Error;
      throw ToolError.validation(`Invalid JSON: ${error.message}`, {
        suggestion: 'Check for missing commas, quotes, or brackets',
      });
    }

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw ToolError.validation('TOML requires a top-level object (table). Arrays and primitives are not valid TOML root values.', {
        suggestion: 'Wrap your data in an object, e.g. { "key": value }',
      });
    }

    if (config.sortKeys) {
      parsed = sortObjectKeys(parsed);
    }

    try {
      return stringifyToml(parsed as Record<string, unknown>);
    } catch (e) {
      const error = e as Error;
      throw ToolError.execution(`Failed to stringify TOML: ${error.message}`);
    }
  },
};

function sortObjectKeys(obj: unknown): unknown {
  if (Array.isArray(obj)) {
    return obj.map(sortObjectKeys);
  }

  if (obj !== null && typeof obj === 'object') {
    const sorted: Record<string, unknown> = {};
    const keys = Object.keys(obj as Record<string, unknown>).sort();
    for (const key of keys) {
      sorted[key] = sortObjectKeys((obj as Record<string, unknown>)[key]);
    }
    return sorted;
  }

  return obj;
}
